import type { Thresholds } from "./policy";
import { attainment, compareToTarget } from "./targets";

/**
 * Health scoring engine (§6): dual system.
 * KPI score  = weighted % of metrics met (boolean compare), weights = kpi_weight.
 * Goal score = weighted mean of direction-aware attainments, weights = goal_weight.
 * Propagation: parent = weighted mean of (own metric score, children's scores).
 * Honesty rule: completeness below cutoff → insufficient_data, never red.
 */

export type Band = "great" | "regular" | "average" | "low" | "insufficient_data";
export type Direction = "gte" | "lte" | "gt" | "lt" | "eq" | "between";

export interface MetricScoreInput {
  metricId: string;
  name: string;
  direction: Direction;
  kpiWeight: number;
  goalWeight: number;
  /** penalty-only metrics can never lift a score — they only drag it when they miss */
  penaltyOnly?: boolean;
  /** rolled-up actual for the period; null = no data */
  actual: number | null;
  /** resolved target; null = untracked */
  target: number | null;
  /** upper bound when direction = between */
  targetMax?: number | null;
  /** expected entries in the period (0 = seat off / not applicable) */
  expected: number;
  completeness: number;
}

export interface SeatScores {
  kpiScore: number | null;
  goalScore: number | null;
  completeness: number;
  /** metrics that actually participated */
  scored: number;
}

export interface ChildHealthInput {
  seatId: string;
  name: string;
  weight: number;
  kpiScore: number | null;
  goalScore: number | null;
  completeness: number;
}

export interface Contributor {
  kind: "metric" | "child";
  id: string;
  name: string;
  weight: number;
  /** shortfall vs 100% attainment, in score points (0 = at/above target) */
  shortfall: number;
  /** weight × shortfall — the ranking key */
  impact: number;
}

/** Score a seat's OWN metrics for one period. Untracked/inapplicable metrics are skipped. */
export function scoreOwnMetrics(metrics: MetricScoreInput[], attainmentCap: number): SeatScores {
  const participating = metrics.filter(
    (m) => m.target !== null && m.expected > 0 && (m.goalWeight > 0 || m.kpiWeight > 0),
  );
  if (participating.length === 0)
    return { kpiScore: null, goalScore: null, completeness: 1, scored: 0 };

  let kpiW = 0,
    kpiMet = 0,
    goalW = 0,
    goalSum = 0;
  let compW = 0,
    compSum = 0;
  for (const m of participating) {
    compW += 1;
    compSum += m.completeness;
    if (m.actual === null) continue; // no data → contributes to (in)completeness only
    const met = compareToTarget(m.direction, m.actual, m.target!, m.targetMax ?? null);
    const att = attainment(m.direction, m.actual, m.target!, attainmentCap, m.targetMax ?? null);
    // penalty-only: a passing penalty metric is excluded entirely (it can't lift);
    // a failing one enters with full weight (it drags)
    if (!(m.penaltyOnly && met)) {
      kpiW += m.kpiWeight;
      if (met) kpiMet += m.kpiWeight;
    }
    if (!(m.penaltyOnly && att >= 1)) {
      goalW += m.goalWeight;
      goalSum += m.goalWeight * att;
    }
  }
  return {
    kpiScore: kpiW > 0 ? (kpiMet / kpiW) * 100 : null,
    goalScore: goalW > 0 ? (goalSum / goalW) * 100 : null,
    completeness: compW > 0 ? compSum / compW : 1,
    scored: participating.length,
  };
}

export interface PropagatedHealth {
  kpiScore: number | null;
  goalScore: number | null;
  completeness: number;
  contributors: Contributor[];
}

/** Combine a seat's own scores with its children's propagated scores. */
export function propagate(
  own: SeatScores,
  ownMetrics: MetricScoreInput[],
  ownMetricsWeight: number,
  children: ChildHealthInput[],
  attainmentCap: number,
): PropagatedHealth {
  const parts: { w: number; kpi: number | null; goal: number | null; comp: number }[] = [];
  if (own.scored > 0)
    parts.push({
      w: ownMetricsWeight,
      kpi: own.kpiScore,
      goal: own.goalScore,
      comp: own.completeness,
    });
  for (const c of children)
    parts.push({ w: c.weight, kpi: c.kpiScore, goal: c.goalScore, comp: c.completeness });

  const agg = (sel: (p: (typeof parts)[number]) => number | null) => {
    let w = 0,
      sum = 0;
    for (const p of parts) {
      const v = sel(p);
      if (v === null) continue;
      w += p.w;
      sum += p.w * v;
    }
    return w > 0 ? sum / w : null;
  };
  const kpiScore = agg((p) => p.kpi);
  const goalScore = agg((p) => p.goal);
  // completeness averages over ALL parts (a silent child drags completeness down)
  const totalW = parts.reduce((s, p) => s + p.w, 0);
  const completeness = totalW > 0 ? parts.reduce((s, p) => s + p.w * p.comp, 0) / totalW : 1;

  // top negative contributors: children + own metrics, ranked by weight × shortfall
  const contributors: Contributor[] = [];
  for (const c of children) {
    if (c.goalScore === null) continue;
    const shortfall = Math.max(0, 100 - c.goalScore);
    if (shortfall > 0)
      contributors.push({
        kind: "child",
        id: c.seatId,
        name: c.name,
        weight: c.weight,
        shortfall,
        impact: c.weight * shortfall,
      });
  }
  for (const m of ownMetrics) {
    if (m.actual === null || m.target === null || m.expected === 0) continue;
    const att = attainment(m.direction, m.actual, m.target, attainmentCap, m.targetMax ?? null);
    const shortfall = Math.max(0, 100 - att * 100);
    if (shortfall > 0)
      contributors.push({
        kind: "metric",
        id: m.metricId,
        name: m.name,
        weight: m.goalWeight,
        shortfall,
        impact: m.goalWeight * shortfall,
      });
  }
  contributors.sort((a, b) => b.impact - a.impact);
  return { kpiScore, goalScore, completeness, contributors };
}

/** Map a score to a band; the honesty rule is applied by the caller via completeness. */
export function bandFor(score: number | null, t: Thresholds): Band {
  if (score === null) return "insufficient_data";
  if (score >= t.great) return "great";
  if (score >= t.regular) return "regular";
  if (score >= t.low) return "average";
  return "low";
}

/** Final band with the honesty rule: below-cutoff completeness is grey, never red. */
export function finalBand(
  score: number | null,
  completeness: number,
  cutoff: number,
  t: Thresholds,
): Band {
  if (score === null || completeness < cutoff) return "insufficient_data";
  return bandFor(score, t);
}

/** Band colors from the validated status palette (dataviz skill). Bands are
 * always rendered WITH the score text and a label/tooltip — never color alone. */
export const BAND_COLORS: Record<Band, string> = {
  great: "#0ca30c", // good
  regular: "#84cc16", // light green (spec §6: green / light-green / orange / red)
  average: "#fab219", // warning
  low: "#d03b3b", // critical
  insufficient_data: "#898781", // muted — missing data is grey, never red
};

export const BAND_LABELS: Record<Band, string> = {
  great: "Great",
  regular: "Regular",
  average: "Average",
  low: "Low",
  insufficient_data: "No data",
};
